/* ==========================================================================
   INGREDIENT GLOBE
   Two entirely independent Three.js layers:
     1. A full-bleed starfield canvas (data-starfield-canvas) - a drifting
        gold particle field with its own scene/camera, auto-rotating on its
        own clock. Purely decorative background; never touched by input.
     2. The interactive globe canvas (data-globe-canvas) - a faint gold
        line-art wireframe traced over a noise-displaced, knobby sphere
        (an uneven gourd/seed-pod silhouette rather than a crystalline
        icosahedron) + core glow, dragged/auto-rotated by the visitor. Every
        ingredient is a REAL <button> in the DOM (not a canvas texture) -
        each frame its 3D position is projected to screen coordinates and
        applied as inline styles, so it's genuinely clickable and
        keyboard/screen-reader accessible regardless of where it has
        rotated to. Buttons on the far hemisphere drop out of the tab order
        and become unclickable rather than sitting there as invisible traps.
   Falls back to a plain static grid (no rotation, no starfield) when WebGL
   is unavailable or the visitor prefers reduced motion - the buttons and
   panel work identically either way.

   Boot is deferred to the window 'load' event, same as theme.js's hero
   effect. This section loads three.min.js with 'defer' immediately before
   this file's own script tag, so THREE is normally already defined by the
   time this file executes - but the `typeof THREE === 'undefined'` guards
   stay in place in case that asset fails to fetch.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-ingredient-globe]');
  if (!root) return;

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var stage = root.querySelector('[data-globe-stage]');
  var canvas = root.querySelector('[data-globe-canvas]');
  var starfieldCanvas = root.querySelector('[data-starfield-canvas]');
  var buttons = Array.prototype.slice.call(root.querySelectorAll('[data-globe-btn]'));

  var panel = root.querySelector('[data-globe-panel]');
  var panelMedia = root.querySelector('[data-globe-panel-media]');
  var panelName = root.querySelector('[data-globe-panel-name]');
  var panelBlurb = root.querySelector('[data-globe-panel-blurb]');
  var panelCloseEls = root.querySelectorAll('[data-globe-panel-close]');
  var lastFocused = null;

  /* ======================================================================
     DETAIL PANEL - works the same whether the globe is 3D or the fallback.
     ====================================================================== */
  function onPanelKeydown(e) {
    if (e.key === 'Escape') closePanel();
  }

  function openPanel(button) {
    if (!panel) return;
    panelName.textContent = button.dataset.name || '';
    panelBlurb.textContent = button.dataset.blurb || '';
    panelMedia.innerHTML = '';
    var media = button.querySelector('img, svg');
    if (media) panelMedia.appendChild(media.cloneNode(true));

    lastFocused = document.activeElement;
    panel.hidden = false;
    document.addEventListener('keydown', onPanelKeydown);
    var closeBtn = root.querySelector('.ingredient-globe__panel-close');
    if (closeBtn) closeBtn.focus();
  }

  function closePanel() {
    if (!panel || panel.hidden) return;
    panel.hidden = true;
    document.removeEventListener('keydown', onPanelKeydown);
    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
  }

  buttons.forEach(function (btn) {
    btn.addEventListener('click', function () { openPanel(btn); });
  });
  panelCloseEls.forEach(function (el) { el.addEventListener('click', closePanel); });

  /* ======================================================================
     SHARED HELPERS
     ====================================================================== */
  function fibonacciSphere(count, radius) {
    var points = [];
    var goldenAngle = Math.PI * (3 - Math.sqrt(5));
    for (var i = 0; i < count; i++) {
      var y = count === 1 ? 0 : 1 - (i / (count - 1)) * 2;
      var r = Math.sqrt(Math.max(0, 1 - y * y));
      var theta = goldenAngle * i;
      points.push(new THREE.Vector3(Math.cos(theta) * r * radius, y * radius, Math.sin(theta) * r * radius));
    }
    return points;
  }

  // A soft round gold glow, drawn once to a canvas and reused as the
  // particle-dust sprite texture and the pulsing core glow - no image asset
  // needed, and it tints cleanly since the material's own color multiplies it.
  function makeGlowTexture() {
    var size = 128;
    var c = document.createElement('canvas');
    c.width = c.height = size;
    var ctx = c.getContext('2d');
    var g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
    g.addColorStop(0, 'rgba(255,255,255,1)');
    g.addColorStop(0.35, 'rgba(255,255,255,.7)');
    g.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, size, size);
    return new THREE.CanvasTexture(c);
  }

  // Earthy dust palette - amber/terracotta/moss instead of a single flat
  // gold, so the starfield reads as pollen/spore drift rather than uniform
  // metallic sparkle. Cream is rare, reserved for the odd catch-light.
  // Kept as plain hex numbers (not THREE.Color instances) because this array
  // is built at parse time, before three.min.js is guaranteed to have loaded -
  // THREE.Color() only gets constructed lazily, inside pickDustColor() below,
  // which never runs before boot() does.
  var DUST_PALETTE = [
    { hex: 0xc9a15e, weight: 0.42 }, // amber
    { hex: 0xa8683f, weight: 0.30 }, // terracotta
    { hex: 0x6b7a4f, weight: 0.24 }, // moss
    { hex: 0xf5e7c3, weight: 0.04 }  // cream highlight
  ];
  function pickDustColor() {
    var pick = Math.random(), acc = 0;
    for (var i = 0; i < DUST_PALETTE.length; i++) {
      acc += DUST_PALETTE[i].weight;
      if (pick <= acc) return new THREE.Color(DUST_PALETTE[i].hex);
    }
    return new THREE.Color(DUST_PALETTE[0].hex);
  }

  function buildParticles(glowTex, innerRadius, count, pointSize, opacity) {
    var geo = new THREE.BufferGeometry();
    var positions = new Float32Array(count * 3);
    var colors = new Float32Array(count * 3);
    for (var i = 0; i < count; i++) {
      var u = Math.random(), v = Math.random();
      var theta = u * Math.PI * 2;
      var phi = Math.acos(2 * v - 1);
      var r = innerRadius * (1.15 + Math.random() * 1.3);
      positions[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      positions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      positions[i * 3 + 2] = r * Math.cos(phi);

      var c = pickDustColor();
      var shade = 0.85 + Math.random() * 0.3; // slight per-mote lightness variation
      colors[i * 3] = c.r * shade;
      colors[i * 3 + 1] = c.g * shade;
      colors[i * 3 + 2] = c.b * shade;
    }
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    var mat = new THREE.PointsMaterial({
      map: glowTex, color: 0xffffff, vertexColors: true, size: pointSize, transparent: true,
      opacity: opacity, depthWrite: false, blending: THREE.AdditiveBlending, sizeAttenuation: true
    });
    return new THREE.Points(geo, mat);
  }

  /* ======================================================================
     ORGANIC WIREFRAME GEOMETRY
     Same thin gold line-art look as the original build (LineSegments over
     EdgesGeometry), but the sphere it's traced from isn't a perfect
     icosahedron any more - each vertex is pushed in or out along its own
     radius by a cheap 3D value-noise fbm, so the facets settle into an
     uneven, knobby gourd/seed-pod silhouette instead of a crystalline gem.
     Computed once at init (not per-frame) - a plain JS port of the same
     hash/noise/fbm approach used elsewhere in this theme, just walking the
     geometry's position attribute instead of running in a shader.
     ====================================================================== */
  function frac(x) { return x - Math.floor(x); }
  function hash3(x, y, z) {
    x = frac(x * 0.3183099 + 0.1);
    y = frac(y * 0.3183099 + 0.1);
    z = frac(z * 0.3183099 + 0.1);
    x *= 17.0; y *= 17.0; z *= 17.0;
    return frac(x * y * z * (x + y + z));
  }
  function noise3(x, y, z) {
    var ix = Math.floor(x), iy = Math.floor(y), iz = Math.floor(z);
    var fx = x - ix, fy = y - iy, fz = z - iz;
    fx = fx * fx * (3 - 2 * fx);
    fy = fy * fy * (3 - 2 * fy);
    fz = fz * fz * (3 - 2 * fz);
    var x00 = hash3(ix, iy, iz) + (hash3(ix + 1, iy, iz) - hash3(ix, iy, iz)) * fx;
    var x10 = hash3(ix, iy + 1, iz) + (hash3(ix + 1, iy + 1, iz) - hash3(ix, iy + 1, iz)) * fx;
    var x01 = hash3(ix, iy, iz + 1) + (hash3(ix + 1, iy, iz + 1) - hash3(ix, iy, iz + 1)) * fx;
    var x11 = hash3(ix, iy + 1, iz + 1) + (hash3(ix + 1, iy + 1, iz + 1) - hash3(ix, iy + 1, iz + 1)) * fx;
    var y0 = x00 + (x10 - x00) * fy;
    var y1 = x01 + (x11 - x01) * fy;
    return y0 + (y1 - y0) * fz;
  }
  function fbm3(x, y, z) {
    var v = 0, a = 0.5;
    for (var i = 0; i < 3; i++) {
      v += a * noise3(x, y, z);
      x *= 2.03; y *= 2.03; z *= 2.03;
      a *= 0.5;
    }
    return v;
  }

  function buildOrganicWireframe(radius, detail, amplitude, freq, seed) {
    var geo = new THREE.IcosahedronGeometry(radius, detail);
    var pos = geo.attributes.position;
    for (var i = 0; i < pos.count; i++) {
      var x = pos.getX(i), y = pos.getY(i), z = pos.getZ(i);
      var len = Math.sqrt(x * x + y * y + z * z) || 1;
      var nx = x / len, ny = y / len, nz = z / len;
      var bump = fbm3(nx * freq + seed, ny * freq + seed, nz * freq + seed);
      var newLen = radius + (bump - 0.5) * amplitude;
      pos.setXYZ(i, nx * newLen, ny * newLen, nz * newLen);
    }
    pos.needsUpdate = true;
    var edges = new THREE.EdgesGeometry(geo);
    return new THREE.LineSegments(edges, new THREE.LineBasicMaterial({ color: 0xc9a15e, transparent: true, opacity: 0.16 }));
  }

  /* ======================================================================
     LAYER 1 - STARFIELD (full-bleed, fully independent, never interactive)
     ====================================================================== */
  function initStarfield() {
    if (REDUCED || typeof THREE === 'undefined' || !starfieldCanvas) return false;

    var renderer;
    try {
      renderer = new THREE.WebGLRenderer({ canvas: starfieldCanvas, antialias: true, alpha: true });
    } catch (err) {
      return false;
    }
    renderer.setClearColor(0x000000, 0);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));

    var scene = new THREE.Scene();
    var camera = new THREE.PerspectiveCamera(55, 1, 0.1, 100);
    camera.position.set(0, 0, 7);
    camera.lookAt(0, 0, 0);

    // Sparse and dim enough to read as a distant backdrop, not competing with
    // the interactive globe in front of it - but still visibly there.
    var particles = buildParticles(makeGlowTexture(), 4, 170, 0.085, 0.4);
    scene.add(particles);

    function resize() {
      var rect = root.getBoundingClientRect();
      renderer.setSize(rect.width, rect.height, false);
      camera.aspect = rect.width / Math.max(rect.height, 1);
      camera.updateProjectionMatrix();
    }
    new ResizeObserver(resize).observe(root);
    resize();

    var running = false, rafId = null, lastTs = 0;
    function frame(ts) {
      if (!running) return;
      var dt = lastTs ? Math.min(ts - lastTs, 50) : 16;
      lastTs = ts;
      particles.rotation.y += 0.00006 * dt;
      particles.rotation.x += 0.000025 * dt;
      renderer.render(scene, camera);
      rafId = requestAnimationFrame(frame);
    }
    function start() { if (!running) { running = true; lastTs = 0; rafId = requestAnimationFrame(frame); } }
    function stop() { running = false; if (rafId) cancelAnimationFrame(rafId); rafId = null; }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries[0].isIntersecting ? start() : stop();
      }, { threshold: 0.01 }).observe(root);
    } else {
      start();
    }
    document.addEventListener('visibilitychange', function () {
      document.hidden ? stop() : start();
    });

    return true;
  }

  /* ======================================================================
     LAYER 2 - INTERACTIVE GLOBE (drag-to-rotate, with inertia)
     ====================================================================== */
  function initGlobe() {
    if (REDUCED || typeof THREE === 'undefined' || !canvas || !buttons.length) return false;

    var renderer;
    try {
      renderer = new THREE.WebGLRenderer({ canvas: canvas, antialias: true, alpha: true });
    } catch (err) {
      return false;
    }
    renderer.setClearColor(0x000000, 0);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));

    var RADIUS = 2;
    var POINT_RADIUS = RADIUS * 1.15;
    var CAMERA_DIST = 7;
    var FRONT_THRESHOLD = 0.5; // 0..1 depth factor above which a button counts as "front-facing"

    var scene = new THREE.Scene();
    var camera = new THREE.PerspectiveCamera(50, 1, 0.1, 100);
    camera.position.set(0, 0, CAMERA_DIST);
    camera.lookAt(0, 0, 0);

    // Knobby, organic line-art sphere - see buildOrganicWireframe() above.
    var wireframe = buildOrganicWireframe(RADIUS, 2, RADIUS * 0.5, 1.7, Math.random() * 100);
    scene.add(wireframe);

    // Pulsing core glow - a big soft additive sprite at the centre.
    var coreMat = new THREE.SpriteMaterial({
      map: makeGlowTexture(), color: 0xc9a15e, transparent: true, opacity: 0.3,
      depthWrite: false, blending: THREE.AdditiveBlending
    });
    var core = new THREE.Sprite(coreMat);
    core.scale.set(RADIUS * 1.7, RADIUS * 1.7, 1);
    scene.add(core);

    var basePoints = fibonacciSphere(buttons.length, POINT_RADIUS);

    // Independent gentle bob per ingredient, purely a CSS animation - phase
    // and duration vary per button so they float out of sync with each other.
    buttons.forEach(function (btn, i) {
      btn.style.setProperty('--bob-delay', ((i * 0.53) % 3.6).toFixed(2) + 's');
      btn.style.setProperty('--bob-duration', (3.2 + (i % 5) * 0.35).toFixed(2) + 's');
    });

    var yaw = 0.4;
    var pitch = -0.15;
    var yawVel = 0;   // radians/ms, carries momentum after release
    var pitchVel = 0;
    var isDragging = false;
    var dragLastX = 0, dragLastY = 0, dragLastTs = 0;
    var lastInteraction = 0;
    var focusWithin = false;
    var stageRect = { width: 1, height: 1 };

    var hoverDir = 0; // -1, 0, or 1 - continuous rotation while an arrow is hovered/focused

    var DRAG_SENSITIVITY = 0.008;
    var ROTATE_STEP = 0.35;
    var HOVER_SPEED = 0.9; // radians/sec while an arrow is hovered/focused
    var AUTO_SPEED = 0.12; // radians/sec, idle auto-rotate
    var IDLE_DELAY_MS = 1200; // pause after release/interaction before auto-rotate resumes
    var VELOCITY_DAMPING = 0.0025; // per-ms decay rate for inertia after release
    var xAxis = new THREE.Vector3(1, 0, 0);
    var yAxis = new THREE.Vector3(0, 1, 0);

    function markInteracted() { lastInteraction = performance.now(); }

    function onPointerDown(e) {
      // Don't hijack presses that land on a real button (ingredient buttons,
      // rotate arrows) - capturing the pointer here would break their native
      // click, since capture retargets the matching pointerup away from them.
      if (e.target.closest('[data-globe-btn], [data-globe-rotate]')) return;
      isDragging = true;
      dragLastX = e.clientX; dragLastY = e.clientY; dragLastTs = performance.now();
      yawVel = 0; pitchVel = 0;
      try { stage.setPointerCapture(e.pointerId); } catch (err) { /* not critical */ }
      markInteracted();
    }
    function onPointerMove(e) {
      if (!isDragging) return;
      var now = performance.now();
      var dt = Math.max(now - dragLastTs, 1);
      var dx = e.clientX - dragLastX;
      var dy = e.clientY - dragLastY;
      dragLastX = e.clientX; dragLastY = e.clientY; dragLastTs = now;

      var dYaw = dx * DRAG_SENSITIVITY;
      var dPitch = dy * DRAG_SENSITIVITY;
      yaw += dYaw;
      pitch = Math.max(-1.3, Math.min(1.3, pitch + dPitch));
      // Track velocity so releasing mid-swipe carries momentum (inertia).
      yawVel = dYaw / dt;
      pitchVel = dPitch / dt;
      markInteracted();
    }
    function endDrag(e) {
      if (!isDragging) return;
      isDragging = false;
      if (e && e.pointerId != null) {
        try { stage.releasePointerCapture(e.pointerId); } catch (err) { /* not critical */ }
      }
      markInteracted();
    }
    stage.addEventListener('pointerdown', onPointerDown);
    stage.addEventListener('pointermove', onPointerMove);
    stage.addEventListener('pointerup', endDrag);
    stage.addEventListener('pointercancel', endDrag);

    var rotateButtons = Array.prototype.slice.call(root.querySelectorAll('[data-globe-rotate]'));
    var rotateEngaged = new WeakMap();

    rotateButtons.forEach(function (btn) {
      var dir = parseFloat(btn.dataset.globeRotate);
      // Quick nudge for a plain tap/click (no hover available, e.g. touch).
      btn.addEventListener('click', function () {
        yaw += dir * ROTATE_STEP;
        yawVel = 0; pitchVel = 0;
        markInteracted();
      });
      // Keyboard parity with hover: continuous spin while focused.
      btn.addEventListener('focus', function () { hoverDir = dir; yawVel = 0; pitchVel = 0; markInteracted(); });
      btn.addEventListener('blur', function () { hoverDir = 0; markInteracted(); });
    });

    // Hover is checked as real circle-vs-point geometry (distance from the
    // button's centre vs. its radius) rather than relying on the browser's
    // own hit-testing, so the entire interior engages - not just near the
    // rendered ring line.
    function updateRotateHover(e) {
      rotateButtons.forEach(function (btn) {
        var dir = parseFloat(btn.dataset.globeRotate);
        var rect = btn.getBoundingClientRect();
        var cx = rect.left + rect.width / 2;
        var cy = rect.top + rect.height / 2;
        var radius = rect.width / 2;
        var inside = Math.hypot(e.clientX - cx, e.clientY - cy) <= radius;
        var wasInside = rotateEngaged.get(btn) || false;
        if (inside && !wasInside) {
          rotateEngaged.set(btn, true);
          btn.classList.add('is-engaged');
          hoverDir = dir; yawVel = 0; pitchVel = 0;
          markInteracted();
        } else if (!inside && wasInside) {
          rotateEngaged.set(btn, false);
          btn.classList.remove('is-engaged');
          if (hoverDir === dir) hoverDir = 0;
          markInteracted();
        }
      });
    }
    window.addEventListener('pointermove', updateRotateHover);
    root.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') { yaw -= ROTATE_STEP; yawVel = 0; pitchVel = 0; markInteracted(); }
      else if (e.key === 'ArrowRight') { yaw += ROTATE_STEP; yawVel = 0; pitchVel = 0; markInteracted(); }
    });

    var buttonsWrap = root.querySelector('[data-globe-buttons]');
    buttonsWrap.addEventListener('focusin', function () { focusWithin = true; });
    buttonsWrap.addEventListener('focusout', function () { focusWithin = false; markInteracted(); });

    function resize() {
      var rect = stage.getBoundingClientRect();
      stageRect = rect;
      renderer.setSize(rect.width, rect.height, false);
      camera.aspect = rect.width / Math.max(rect.height, 1);
      camera.updateProjectionMatrix();
    }
    var ro = new ResizeObserver(resize);
    ro.observe(stage);
    resize();

    var running = false, rafId = null, lastTs = 0;

    function frame(ts) {
      if (!running) return;
      var dt = lastTs ? Math.min(ts - lastTs, 50) : 16;
      lastTs = ts;
      var now = performance.now();

      if (isDragging) {
        // position/velocity already updated live in onPointerMove
      } else if (hoverDir !== 0) {
        yaw += hoverDir * HOVER_SPEED * (dt / 1000);
      } else if (Math.abs(yawVel) > 0.00002 || Math.abs(pitchVel) > 0.00002) {
        // Momentum: keep spinning with decaying velocity after release.
        yaw += yawVel * dt;
        pitch = Math.max(-1.3, Math.min(1.3, pitch + pitchVel * dt));
        var decay = Math.pow(VELOCITY_DAMPING, dt / 1000);
        yawVel *= decay;
        pitchVel *= decay;
      } else if (!focusWithin && (now - lastInteraction > IDLE_DELAY_MS)) {
        yaw += AUTO_SPEED * (dt / 1000);
      }

      var qYaw = new THREE.Quaternion().setFromAxisAngle(yAxis, yaw);
      var qPitch = new THREE.Quaternion().setFromAxisAngle(xAxis, pitch);
      var combined = qYaw.multiply(qPitch);
      wireframe.quaternion.copy(combined);
      var breathe = 1 + Math.sin(now * 0.00055) * 0.02;
      wireframe.scale.setScalar(breathe);
      core.material.opacity = 0.24 + Math.sin(now * 0.0009) * 0.08;

      for (var i = 0; i < buttons.length; i++) {
        var rotated = basePoints[i].clone().applyQuaternion(combined);
        var projected = rotated.clone().project(camera);
        var screenX = (projected.x + 1) / 2 * stageRect.width;
        var screenY = (1 - (projected.y + 1) / 2) * stageRect.height;
        var depthFactor = (rotated.z + POINT_RADIUS) / (2 * POINT_RADIUS);
        var scale = 0.55 + depthFactor * 0.6;
        var opacity = 0.18 + depthFactor * 0.82;
        var isFront = depthFactor >= FRONT_THRESHOLD;

        var btn = buttons[i];
        btn.style.left = screenX + 'px';
        btn.style.top = screenY + 'px';
        btn.style.setProperty('--globe-scale', scale.toFixed(3));
        btn.style.setProperty('--globe-opacity', opacity.toFixed(3));
        btn.style.zIndex = Math.round(depthFactor * 100);

        if (isFront) {
          btn.style.pointerEvents = 'auto';
          if (document.activeElement !== btn) btn.removeAttribute('tabindex');
          btn.removeAttribute('aria-hidden');
        } else {
          btn.style.pointerEvents = 'none';
          if (document.activeElement !== btn) btn.setAttribute('tabindex', '-1');
          btn.setAttribute('aria-hidden', 'true');
        }
      }

      renderer.render(scene, camera);
      rafId = requestAnimationFrame(frame);
    }

    function start() { if (!running) { running = true; lastTs = 0; rafId = requestAnimationFrame(frame); } }
    function stop() { running = false; if (rafId) cancelAnimationFrame(rafId); rafId = null; }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries[0].isIntersecting ? start() : stop();
      }, { threshold: 0.01 }).observe(stage);
    } else {
      start();
    }
    document.addEventListener('visibilitychange', function () {
      document.hidden ? stop() : start();
    });

    return true;
  }

  function boot() {
    initStarfield();
    var is3D = initGlobe();
    if (is3D) stage.classList.add('is-3d');
  }

  if (document.readyState === 'complete') { boot(); }
  else { window.addEventListener('load', boot); }
})();
